import React, { useState, useRef, useEffect } from 'react';
import { audiences, intents, interestLabels, matchLines, buildBrief } from '../data/brief.js';
import { submitBrief, getCheckoutUrl } from '../lib/briefApi.js';
import { useContent } from '../cms/content.js';

const chipBase = {
  padding: '10px 15px',
  border: '1px solid rgba(201,168,106,0.28)',
  borderRadius: '999px',
  background: 'transparent',
  color: '#b9b3a6',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  transition: 'all .25s',
  userSelect: 'none',
};

const chipSel = {
  ...chipBase,
  background: 'rgba(201,168,106,0.16)',
  borderColor: '#c9a86a',
  color: '#f5f0e6',
};

const intentBase = {
  flex: '1 1 140px',
  padding: '15px 14px',
  border: '1px solid rgba(201,168,106,0.22)',
  background: 'transparent',
  color: '#b9b3a6',
  cursor: 'pointer',
  borderRadius: '2px',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  transition: 'all .25s',
};

const intentSel = {
  ...intentBase,
  background: 'rgba(201,168,106,0.14)',
  borderColor: '#c9a86a',
  color: '#f5f0e6',
};

export default function BriefForm() {
  const cms = useContent();
  const [step, setStep] = useState('intake');
  const [audience, setAudience] = useState('Individual');
  const [intent, setIntent] = useState('Relocation');
  const [interests, setInterests] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [matchIdx, setMatchIdx] = useState(0);
  const [payPhase, setPayPhase] = useState('connecting');

  const briefRef = useRef(null);
  const matchIntervalRef = useRef(null);
  const payTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (matchIntervalRef.current) clearInterval(matchIntervalRef.current);
      if (payTimeoutRef.current) clearTimeout(payTimeoutRef.current);
    };
  }, []);

  const toggleInterest = (label) => {
    setInterests((prev) => {
      if (prev.includes(label)) {
        return prev.filter((i) => i !== label);
      } else {
        return [...prev, label];
      }
    });
  };

  const submit = () => {
    submitBrief({ audience, intent, interests, name, channel: email }).catch(() => {});
    if (matchIntervalRef.current) clearInterval(matchIntervalRef.current);
    setStep('matching');
    setMatchIdx(0);

    matchIntervalRef.current = setInterval(() => {
      setMatchIdx((prevIdx) => {
        const next = prevIdx + 1;
        if (next >= matchLines.length) {
          clearInterval(matchIntervalRef.current);
          matchIntervalRef.current = null;

          payTimeoutRef.current = setTimeout(() => {
            setStep('result');
            payTimeoutRef.current = null;
          }, 650);

          return matchLines.length - 1;
        }
        return next;
      });
    }, 560);
  };

  const startCheckout = () => {
    // Persist the current brief so the secured dossier page can render it.
    if (briefRef.current) {
      localStorage.setItem('kny_secured_brief', JSON.stringify(briefRef.current));
    }
    const url = getCheckoutUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    if (payTimeoutRef.current) clearTimeout(payTimeoutRef.current);
    setStep('checkout');
    setPayPhase('connecting');
    const redirect = () => {
      window.location.hash = '#/secured';
      payTimeoutRef.current = null;
    };
    if (url) {
      // Real Stripe checkout opened in a new tab — brief connecting UX, then route in.
      payTimeoutRef.current = setTimeout(redirect, 1000);
    } else {
      // Mock / no Stripe configured — route to the secured dossier immediately.
      redirect();
    }
  };

  const reset = () => {
    if (matchIntervalRef.current) {
      clearInterval(matchIntervalRef.current);
      matchIntervalRef.current = null;
    }
    if (payTimeoutRef.current) {
      clearTimeout(payTimeoutRef.current);
      payTimeoutRef.current = null;
    }
    briefRef.current = null;
    setStep('intake');
    setMatchIdx(0);
  };

  const isResult = step === 'result';
  const isCheckout = step === 'checkout';
  const showBrief = isResult || isCheckout;
  if (showBrief && !briefRef.current) {
    briefRef.current = buildBrief(intent, interests);
  }
  const brief = briefRef.current;

  const matchText = matchLines[matchIdx] || '';
  const matchProgress = Math.round(((matchIdx + 1) / matchLines.length) * 100) + '%';
  const matchCount = 120 + matchIdx * 64;

  return (
    <section
      id="brief"
      style={{
        position: 'relative',
        zIndex: 10,
        background: 'linear-gradient(180deg, #0a0a0b, #0c0d10)',
        borderTop: '1px solid rgba(201,168,106,0.14)',
        padding: '130px 32px 150px',
      }}
    >
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div data-reveal="" style={{ textAlign: 'center', marginBottom: '50px' }}>
          <div
            style={{
              font: "500 12px/1 'JetBrains Mono',monospace",
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: '#c9a86a',
              marginBottom: '24px',
            }}
          >
            {cms.brief.eyebrow}
          </div>
          <h2
            style={{
              margin: '0 auto 18px',
              fontFamily: "'Cormorant',serif",
              fontWeight: 400,
              fontSize: 'clamp(40px,5.4vw,68px)',
              lineHeight: 0.98,
              color: '#f5f0e6',
            }}
          >
            {cms.brief.heading}
          </h2>
          <p
            style={{
              margin: '0 auto',
              maxWidth: '480px',
              fontSize: '16px',
              lineHeight: 1.6,
              fontWeight: 300,
              color: '#8b8579',
            }}
          >
            {cms.brief.subhead}
          </p>
        </div>
        <div
          data-reveal=""
          style={{
            border: '1px solid rgba(201,168,106,0.22)',
            background: 'rgba(12,13,16,0.72)',
            backdropFilter: 'blur(8px)',
            padding: '46px',
          }}
        >
          {/* INTAKE */}
          {step === 'intake' && (
            <div>
              <div style={{ marginBottom: '30px' }}>
                <label
                  style={{
                    display: 'block',
                    font: "500 11px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#c9a86a',
                    marginBottom: '16px',
                  }}
                >
                  I am arranging this as
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {audiences.map((label) => (
                    <button
                      key={label}
                      type="button"
                      className="kny-intent"
                      onClick={() => setAudience(label)}
                      aria-pressed={audience === label}
                      style={audience === label ? intentSel : intentBase}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p
                  style={{
                    margin: '12px 0 0',
                    font: "400 12px/1.5 'Hanken Grotesk',sans-serif",
                    color: '#8b8579',
                  }}
                >
                  {audience === 'Family'
                    ? 'We tailor the brief for a household with children \u2014 schools, space, and neighborhood fit.'
                    : 'A brief tuned to one person\u2019s move and priorities.'}
                </p>
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label
                  style={{
                    display: 'block',
                    font: "500 11px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#c9a86a',
                    marginBottom: '16px',
                  }}
                >
                  I am looking to
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {intents.map((label) => (
                    <button
                      key={label}
                      type="button"
                      className="kny-intent"
                      onClick={() => setIntent(label)}
                      style={intent === label ? intentSel : intentBase}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label
                  style={{
                    display: 'block',
                    font: "500 11px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#c9a86a',
                    marginBottom: '16px',
                  }}
                >
                  What matters to you <span style={{ color: '#6f6a60' }}>— select any</span>
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {interestLabels.map((label) => (
                    <button
                      key={label}
                      type="button"
                      className="kny-chip"
                      onClick={() => toggleInterest(label)}
                      style={interests.includes(label) ? chipSel : chipBase}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '30px' }}>
                <div>
                  <label
                    style={{
                      display: 'block',
                      font: "500 11px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: '#c9a86a',
                      marginBottom: '12px',
                    }}
                  >
                    Name
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    style={{
                      width: '100%',
                      background: '#0a0a0b',
                      border: '1px solid rgba(201,168,106,0.24)',
                      color: '#ece7dd',
                      padding: '14px 16px',
                      fontFamily: "'Hanken Grotesk',sans-serif",
                      fontSize: '15px',
                      borderRadius: '2px',
                      outline: 'none',
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      font: "500 11px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: '#c9a86a',
                      marginBottom: '12px',
                    }}
                  >
                    Encrypted channel
                  </label>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email or Signal"
                    style={{
                      width: '100%',
                      background: '#0a0a0b',
                      border: '1px solid rgba(201,168,106,0.24)',
                      color: '#ece7dd',
                      padding: '14px 16px',
                      fontFamily: "'Hanken Grotesk',sans-serif",
                      fontSize: '15px',
                      borderRadius: '2px',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              <button
                type="button"
                className="kny-cta"
                onClick={submit}
                style={{
                  width: '100%',
                  background: '#c9a86a',
                  color: '#0a0a0b',
                  border: 'none',
                  padding: '18px',
                  font: "500 12px/1 'JetBrains Mono',monospace",
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  borderRadius: '2px',
                }}
              >
                Submit brief & begin search →
              </button>
              <p
                style={{
                  margin: '20px 0 0',
                  textAlign: 'center',
                  font: "500 10px/1.5 'JetBrains Mono',monospace",
                  letterSpacing: '0.1em',
                  color: '#6f6a60',
                }}
              >
                Confidential · No data shared · Reviewed by a human advisor
              </p>
            </div>
          )}

          {/* MATCHING */}
          {step === 'matching' && (
            <div
              style={{
                minHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  font: "500 11px/1 'JetBrains Mono',monospace",
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  color: '#c9a86a',
                  marginBottom: '30px',
                }}
              >
                Searching the city
              </div>
              <div
                style={{
                  fontFamily: "'Cormorant',serif",
                  fontStyle: 'italic',
                  fontWeight: 300,
                  fontSize: '32px',
                  color: '#f5f0e6',
                  minHeight: '44px',
                }}
              >
                {matchText}
                <span style={{ animation: 'kny-blink 1s steps(1) infinite', color: '#c9a86a' }}>_</span>
              </div>
              <div
                style={{
                  width: '100%',
                  maxWidth: '420px',
                  height: '1px',
                  background: 'rgba(201,168,106,0.18)',
                  marginTop: '38px',
                  overflow: 'hidden',
                }}
              >
                <div style={{ height: '100%', background: '#c9a86a', width: matchProgress, transition: 'width .5s ease' }} />
              </div>
              <div
                style={{
                  font: "500 10px/1 'JetBrains Mono',monospace",
                  letterSpacing: '0.18em',
                  color: '#6f6a60',
                  marginTop: '18px',
                }}
              >
                {matchCount} sources queried
              </div>
            </div>
          )}

          {/* RESULT */}
          {step === 'result' && (
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(201,168,106,0.18)',
                  paddingBottom: '20px',
                  marginBottom: '28px',
                }}
              >
                <div>
                  <div
                    style={{
                      font: "500 10px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: '#6f6a60',
                      marginBottom: '8px',
                    }}
                  >
                    Preliminary Dossier
                  </div>
                  <div style={{ fontFamily: "'Cormorant',serif", fontSize: '27px', color: '#f5f0e6' }}>
                    {brief?.id || ''}
                  </div>
                </div>
                <div
                  style={{
                    font: "500 10px/1 'JetBrains Mono',monospace",
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#c9a86a',
                    border: '1px solid rgba(201,168,106,0.3)',
                    padding: '8px 12px',
                  }}
                >
                  {brief?.intent || ''}
                </div>
              </div>

              <div
                style={{
                  font: "500 11px/1 'JetBrains Mono',monospace",
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: '#c9a86a',
                  marginBottom: '16px',
                }}
              >
                Matched neighborhoods
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px',
                  background: 'rgba(201,168,106,0.14)',
                  border: '1px solid rgba(201,168,106,0.14)',
                  marginBottom: '28px',
                }}
              >
                {(brief?.hoods || []).map((h, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: '#0a0a0b',
                      padding: '18px 20px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px',
                    }}
                  >
                    <span style={{ fontFamily: "'Cormorant',serif", fontSize: '21px', color: '#f5f0e6' }}>
                      {h.name}
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 300, color: '#9b958a', textAlign: 'right' }}>
                      {h.note}
                    </span>
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '1px',
                  background: 'rgba(201,168,106,0.14)',
                  border: '1px solid rgba(201,168,106,0.14)',
                  marginBottom: '30px',
                }}
              >
                <div style={{ background: '#0a0a0b', padding: '20px' }}>
                  <div
                    style={{
                      font: "500 10px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.14em',
                      color: '#6f6a60',
                      marginBottom: '10px',
                    }}
                  >
                    RESIDENCE
                  </div>
                  <div style={{ fontFamily: "'Cormorant',serif", fontSize: '18px', color: '#f5f0e6', lineHeight: 1.2 }}>
                    {brief?.residence || ''}
                  </div>
                </div>
                <div style={{ background: '#0a0a0b', padding: '20px' }}>
                  <div
                    style={{
                      font: "500 10px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.14em',
                      color: '#6f6a60',
                      marginBottom: '10px',
                    }}
                  >
                    OFFICE
                  </div>
                  <div style={{ fontFamily: "'Cormorant',serif", fontSize: '18px', color: '#f5f0e6', lineHeight: 1.2 }}>
                    {brief?.office || ''}
                  </div>
                </div>
                <div style={{ background: '#0a0a0b', padding: '20px' }}>
                  <div
                    style={{
                      font: "500 10px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.14em',
                      color: '#6f6a60',
                      marginBottom: '10px',
                    }}
                  >
                    VENUE
                  </div>
                  <div style={{ fontFamily: "'Cormorant',serif", fontSize: '18px', color: '#f5f0e6', lineHeight: 1.2 }}>
                    {brief?.venue || ''}
                  </div>
                </div>
              </div>

              <p
                style={{
                  margin: '0 0 24px',
                  fontSize: '15px',
                  lineHeight: 1.6,
                  fontWeight: 300,
                  color: '#9b958a',
                  textAlign: 'center',
                }}
              >
                This is the surface. Secure your dossier and a dedicated advisor will reach out by encrypted channel
                within 24 hours.
              </p>
              <button
                type="button"
                className="kny-cta"
                onClick={startCheckout}
                style={{
                  width: '100%',
                  background: '#c9a86a',
                  color: '#0a0a0b',
                  border: 'none',
                  padding: '18px',
                  font: "500 12px/1 'JetBrains Mono',monospace",
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  borderRadius: '2px',
                }}
              >
                Secure your dossier — {cms.fee.amount} via Stripe
              </button>
              <button
                type="button"
                className="kny-underline"
                onClick={reset}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: '#8b8579',
                  border: 'none',
                  padding: '16px 0 0',
                  font: "500 11px/1 'JetBrains Mono',monospace",
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Start a new brief
              </button>
            </div>
          )}

          {/* CHECKOUT (mock Stripe) */}
          {step === 'checkout' && (
            <div
              style={{
                minHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              {payPhase === 'connecting' && (
                <div>
                  <div
                    style={{
                      width: '34px',
                      height: '34px',
                      border: '2px solid rgba(201,168,106,0.25)',
                      borderTopColor: '#c9a86a',
                      borderRadius: '50%',
                      margin: '0 auto 28px',
                      animation: 'kny-spin .9s linear infinite',
                    }}
                  />
                  <div
                    style={{
                      fontFamily: "'Cormorant',serif",
                      fontStyle: 'italic',
                      fontWeight: 300,
                      fontSize: '28px',
                      color: '#f5f0e6',
                    }}
                  >
                    Opening secure Stripe checkout…
                  </div>
                  <div
                    style={{
                      font: "500 10px/1 'JetBrains Mono',monospace",
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: '#6f6a60',
                      marginTop: '18px',
                    }}
                  >
                    Encrypted · PCI compliant
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
